import { useMemo } from 'react';

import { useMyMemberships } from './useMyMemberships';
import { useGetAgencyBranding } from './useGetAgencyBranding';
import { BrandPalette, deriveBrandPalette } from '../utils/derive-brand-palette';

// ----------------------------------------------------------------------

// The authenticated portal pages live at flat /portal/home and /portal/packages — the slug is
// not in the URL. We resolve the active agency's branding frontend-only (ADR-0001 keeps this
// off the backend): a single-membership client uses that membership's slug; a multi-membership
// client uses the slug the landing stashed when it entered this agency's locker.
const ACTIVE_SLUG_KEY = 'portal:activeSlug';

export function stashActiveSlug(slug: string) {
  try {
    sessionStorage.setItem(ACTIVE_SLUG_KEY, slug);
  } catch {
    /* sessionStorage unavailable (private mode) — single-membership path still works */
  }
}

function readStashedSlug(): string | undefined {
  try {
    return sessionStorage.getItem(ACTIVE_SLUG_KEY) ?? undefined;
  } catch {
    return undefined;
  }
}

export function useActiveAgencyBranding(): {
  branding: ReturnType<typeof useGetAgencyBranding>['branding'];
  palette: BrandPalette;
  slug?: string;
  isLoading: boolean;
} {
  const { memberships, isLoading: membershipsLoading } = useMyMemberships();

  const slug = useMemo(() => {
    if (memberships.length === 1) return memberships[0].slug;
    const stashed = readStashedSlug();
    if (stashed && memberships.some((m) => m.slug === stashed)) return stashed;
    // Fall back to the first membership so the chrome still brands rather than going blank.
    return memberships[0]?.slug;
  }, [memberships]);

  const { branding, isLoading: brandingLoading } = useGetAgencyBranding(slug);

  const palette = useMemo(() => deriveBrandPalette(branding?.brandColor), [branding?.brandColor]);

  return { branding, palette, slug, isLoading: membershipsLoading || brandingLoading };
}
